import { getProfileValues, getProfileValuesWithNone } from "building-generation/elements/profileTools";
import type { Building } from "building-types/building/buildingTypes";
import type { ProfileTable } from "building-types/profiles/profileTypes";
import type { GableRoofExtrusion } from "building-types/roofs/gableRoofExtrusionTypes";
import {
  type EaveExtension,
  type RoofWallFaces,
  type GeneratedRoof,
  type GeneratedRoofSystem,
  type UnderRoofTrim,
  type CornerBoardDatumsAABB,
  type GableSubRoofWallFaces,
  ROOF_WALL_FACE_TAG,
} from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import { RoofFaceType } from "building-types/roofs/roofFace";
import type { RoofDormerPlacement } from "building-types/roofs/roofSystemTypes";
import { friezeBoardNew } from "building-types/roofs/roofTrimTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { SettingsToCatalogs } from "building-types/settings/profileCatalogSettings";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { RoofSide } from "building-types/settings/roofTypes";
import type { RWTable, Table } from "common/data/table";
import { roofSettingsKeys } from "doc/settingsUtils";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { aabb3Union } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import { faceAABB3, faceByFourPoints, faceOrNullMove, faceProjectOuterLoopToPlane } from "geom/brep";
import { tagFaces } from "geom/brep";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { offsetPolyLine3InXYPlane } from "geom/offset";
import { planeNewXY, planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3Edge,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3Move,
  polyLine3OnPlane,
  polyLine3Reverse,
  polyLine3ToPolyLine2IgnoreZ,
} from "geom/polyLine3";
import RBush from "rbush";
import { roofFaceNew } from "./roofFaceNew";

export function eaveExtensionNew(
  roofTopFace: RoofFace,
  rail2: PolyLine2,
  rail3: PolyLine3,
  exteriorWallOffsetWithFinish: number,
): EaveExtension {
  return {
    roofTopFace,
    rail2,
    rail3,
    exteriorWallOffsetWithFinish,
  };
}

export function roofWallFacesNew(
  leftFaces: Face[],
  rightFaces: Face[],
  srcFace: Face | null,
  edge: PolyLine2 | null,
  extendedEdge: PolyLine2 | null,
  side: RoofSide,
  addFacadeTrim: boolean,
  thickness: number,
  isGapFillingWall: boolean,
  topFaces: Face[] = [],
  bottomFaces: Face[] = [],
  startFaces: Face[] = [],
  endFaces: Face[] = [],
): RoofWallFaces {
  return {
    leftFaces: tagFaces(leftFaces, ROOF_WALL_FACE_TAG),
    rightFaces: tagFaces(rightFaces, ROOF_WALL_FACE_TAG),
    topFaces: tagFaces(topFaces, ROOF_WALL_FACE_TAG),
    bottomFaces: tagFaces(bottomFaces, ROOF_WALL_FACE_TAG),
    startFaces: tagFaces(startFaces, ROOF_WALL_FACE_TAG),
    endFaces: tagFaces(endFaces, ROOF_WALL_FACE_TAG),
    leftHoleFaces: [],
    rightHoleFaces: [],
    leftExteriorFaces: [],
    rightExteriorFaces: [],
    srcFace,
    edge,
    extendedEdge,
    side,
    addFacadeTrim,
    thickness,
    isGapFillingWall,
  };
}

export function gableSubRoofExtrusionAABB3(extrusion: GableRoofExtrusion): AABB3 | undefined {
  const { startFace, endFace, leftFaces, rightFaces } = extrusion;

  let bb: AABB3 | undefined = undefined;

  const addFace = (face: Face | null) => {
    if (!face) {
      return;
    }

    if (!bb) {
      bb = faceAABB3(face);
    } else {
      bb = aabb3Union(bb, faceAABB3(face));
    }
  };

  const addFaces = (faces: Face[]) => {
    for (const face of faces) {
      addFace(face);
    }
  };

  addFace(startFace);
  addFace(endFace);
  addFaces(leftFaces);
  addFaces(rightFaces);

  return bb;
}

export function multiGableRoofNew(
  roofSystemId: string,
  name: string,
  levelId: string,
  roofVolumes: Table<GeneratedRoofVolumeLegacy>,
  roofs: Table<GeneratedRoof>,
  blockIdToSubRoofId: Table<string>,
  clipContext: MultiGableRoofClipContext,
): GeneratedRoofSystem {
  return {
    ...buildingEntityBaseNew("ROOF_SYSTEM", roofSystemId, { type: "ROOF_SYSTEM", id: roofSystemId }),
    name,
    levelId,
    roofs,
    roofVolumes,
    blockIdToSubRoofId,
    clipContext,
  } as GeneratedRoofSystem;
}

enum RoofFaceGroupType {
  OTHER = "OTHER",
  DORMER = "DORMER",
  RAFTER_EXTENSION = "RAFTER_EXTENSION",
}
interface RoofFaceGroupInfo {
  readonly type: RoofFaceGroupType;
  readonly faces: RoofFace[];
}

type RoofFaceTypeExcludeTable = { [key in RoofFaceGroupType]?: boolean };

export const ONLY_DORMER_FACE_TYPE: RoofFaceTypeExcludeTable = {
  [RoofFaceGroupType.OTHER]: true,
  [RoofFaceGroupType.RAFTER_EXTENSION]: true,
};

export const ONLY_RAFTER_FACE_TYPE: RoofFaceTypeExcludeTable = {
  [RoofFaceGroupType.OTHER]: true,
  [RoofFaceGroupType.DORMER]: true,
};

export const EXCLUDE_RAFTER_FACE_TYPE: RoofFaceTypeExcludeTable = {
  [RoofFaceGroupType.RAFTER_EXTENSION]: true,
};

export function convertRoofFaceInfoToFaceListArray(
  roofFaceInfos: Table<RoofFaceGroupInfo[]>,
  typesToExclude: RoofFaceTypeExcludeTable = {},
): Table<RoofFace[]> {
  const faceListArray: RWTable<RoofFace[]> = {};
  for (const levelId in roofFaceInfos) {
    const faceArray: RoofFace[] = [];
    for (const roofFaceInfo of roofFaceInfos[levelId]) {
      if (!typesToExclude[roofFaceInfo.type]) {
        faceArray.push(...roofFaceInfo.faces);
      }
    }
    faceListArray[levelId] = faceArray;
  }

  return faceListArray;
}

export function getDiscardBSPsByLevel(roofs: Table<GeneratedRoofSystem>, levelId: string): (NodeBSP | null)[] {
  const discardBSPs: (NodeBSP | null)[] = [];

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    if (roof.levelId !== levelId) {
      continue;
    }

    discardBSPs.push(roof.clipContext.discardBSP);
  }

  return discardBSPs;
}

export function getRoofsTopFacesByLevel(
  roofs: Table<GeneratedRoofSystem>,
  levels: Building["levels"],
  result: RWTable<RoofFaceGroupInfo[]> = {},
): Table<RoofFaceGroupInfo[]> {
  for (const roofId in roofs) {
    getRoofTopFacesByLevel(roofs[roofId], levels, result);
  }

  return result;
}

function getRoofTopFacesByLevel(
  roofSystem: GeneratedRoofSystem,
  levels: Building["levels"],
  result: RWTable<RoofFaceGroupInfo[]> = {},
): Table<RoofFaceGroupInfo[]> {
  const { roofs: subRoofs } = roofSystem;

  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];
    const { roofTopFaces, trimsRoofMat, levelId, isDormer, roofSettings, roofPlanSettings, roofElevationPlanSettings } =
      subRoof;

    if (!result[levelId]) {
      result[levelId] = [];
    }

    result[levelId].push({
      type: isDormer ? RoofFaceGroupType.DORMER : RoofFaceGroupType.OTHER,
      faces: roofTopFaces,
    });

    if (roofSettings?.addRafterTailExtension) {
      for (let i = 0, l = trimsRoofMat.length; i < l; i++) {
        result[levelId].push({
          type: RoofFaceGroupType.RAFTER_EXTENSION,
          faces: trimsRoofMat[i].faces.map((face) =>
            roofFaceNew(RoofFaceType.NORMAL, RoofSide.INVALID, face, false, {
              ...roofPlanSettings,
              ...roofElevationPlanSettings,
            }),
          ),
        });
      }
    }
  }

  for (const levelId in levels) {
    if (!result[levelId]) {
      result[levelId] = [];
    }
  }

  return result;
}

/**
 * Get lines representing ridge vent outlines in top-down model space.
 */
export function roofRidgeVentOutlines(building: Building) {
  const roofs = building.roofSystems;
  const ridgeVentsFaces: Face[] = [];
  for (const roofId in roofs) {
    const roof = roofs[roofId];
    for (const subRoofId in roof.roofs) {
      const subRoof = roof.roofs[subRoofId];
      ridgeVentsFaces.push(...subRoof.ridgeVentFaces);
    }
  }
  const ridgeVents2D = ridgeVentsFaces.map((ridgeVentFace) =>
    polyLine3ToPolyLine2IgnoreZ(polyLine3OnPlane(ridgeVentFace.loops[0], ridgeVentFace.plane)),
  );
  return ridgeVents2D;
}

export function gableSubRoofWallFacesNew(
  startWallFaces: RoofWallFaces,
  endWallFaces: RoofWallFaces,
  vaultedRoofCapFaces: RoofWallFaces[] = [],
  hideInteriorFaces: Face[] = [],
  underRoofTrim: UnderRoofTrim[] = [],
  datumsAtCorner: RBush<CornerBoardDatumsAABB> = new RBush<CornerBoardDatumsAABB>(),
): GableSubRoofWallFaces {
  return {
    startWallFaces,
    endWallFaces,
    vaultedRoofCapFaces,
    hideInteriorFaces,
    underRoofTrim,
    datumsAtCorner,
  };
}

export function getRoomSubRoofs(room: Room, roofs: Table<GeneratedRoofSystem>): GeneratedRoof[] {
  const roomSubRoofs: GeneratedRoof[] = [];

  const blockId = room.parentId;

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const subRoofs = roof.roofs;
    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const blockIds = subRoof.roofVolume.blockIds;
      for (const subRoofBlockId of blockIds) {
        if (subRoofBlockId === blockId) {
          roomSubRoofs.push(subRoof);
          break;
        }
      }
    }
  }

  return roomSubRoofs;
}

function getRoofWallFaces(
  subRoof: GeneratedRoof,
  addFacePred: (outFaces: Face[], wallFaces: RoofWallFaces) => void,
  side?: RoofSide,
): Face[] {
  const { startWallFaces, endWallFaces } = subRoof.wallFaces;

  const faces: Face[] = [];

  // start faces
  if (side === undefined || side === RoofSide.START) {
    addFacePred(faces, startWallFaces);
  }

  // end faces
  if (side === undefined || side === RoofSide.END) {
    addFacePred(faces, endWallFaces);
  }

  return faces;
}

export function getExteriorRoofWallFaces(subRoof: GeneratedRoof, side?: RoofSide): Face[] {
  const addFacePred = (outFaces: Face[], wallFaces: RoofWallFaces) => {
    outFaces.push(...wallFaces.leftFaces);
  };

  return getRoofWallFaces(subRoof, addFacePred, side);
}

export function getInteriorRoofWallFaces(subRoof: GeneratedRoof, side?: RoofSide): Face[] {
  const addFacePred = (outFaces: Face[], wallFaces: RoofWallFaces) => {
    outFaces.push(...wallFaces.rightFaces);
  };

  return getRoofWallFaces(subRoof, addFacePred, side);
}

export function getSideRoofWallFaces(subRoof: GeneratedRoof, side?: RoofSide): Face[] {
  const addFacePred = (outFaces: Face[], wallFaces: RoofWallFaces) => {
    outFaces.push(...wallFaces.startFaces);
    outFaces.push(...wallFaces.endFaces);
    outFaces.push(...wallFaces.topFaces);
    outFaces.push(...wallFaces.bottomFaces);
  };

  return getRoofWallFaces(subRoof, addFacePred, side);
}

export function getFinalRoofPlanSettings(
  instanceSettings: Partial<RoofPlanSettings> | undefined,
  roofPlanSettings: RoofPlanSettings,
): RoofPlanSettings {
  const filteredInstanceSettings: Partial<RoofPlanSettings> = {};

  for (const id in instanceSettings) {
    const val = instanceSettings[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  return {
    ...roofPlanSettings,
    ...filteredInstanceSettings,
  };
}

export function getFinalRoofFramingSettings(
  instanceSettings: Partial<RoofFramingSettings>,
  roofFramingSettings: RoofFramingSettings,
): RoofFramingSettings {
  const filteredInstanceSettings: Partial<RoofFramingSettings> = {};
  for (const id in instanceSettings) {
    const val = instanceSettings[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  return {
    ...roofFramingSettings,
    ...filteredInstanceSettings,
  };
}

export function getFinalDormerRoofSettings(roofSettings: RoofSettings, dormerSettings: DormerSettings): RoofSettings {
  const filteredDormerSettings: Partial<DormerSettings> = {};
  const consolidatedRakeFriezeBoardInput: FriezeBoard[] = [];
  const consolidatedEaveFriezeBoardInput: FriezeBoard[] = [];

  for (const id in dormerSettings) {
    const val = dormerSettings[id];

    if (id === "rakeFriezeBoard") {
      consolidatedRakeFriezeBoardInput.push(friezeBoardNew(val, "", dormerSettings["rakeFriezeBoardMaterial"]));
      continue;
    }
    if (id === "eaveFriezeBoard") {
      consolidatedEaveFriezeBoardInput.push(friezeBoardNew(val, "", dormerSettings["eaveFriezeBoardMaterial"]));
      continue;
    }
    if (id === "rakeFriezeBoardMaterial" || id === "eaveFriezeBoardMaterial") {
      continue;
    }

    if (val !== undefined && val !== null) {
      filteredDormerSettings[id] = val;
    }
  }

  let finalDormerSettings = {
    ...roofSettings,
    ...filteredDormerSettings,
  };

  if (consolidatedRakeFriezeBoardInput.length > 0) {
    finalDormerSettings = {
      ...finalDormerSettings,
      rakeFriezeBoard: consolidatedRakeFriezeBoardInput,
    };
    if (dormerSettings["rakeFriezeBoardMaterial"]) {
      finalDormerSettings = {
        ...finalDormerSettings,
        rakeFriezeBoardMaterialLegacy: dormerSettings["rakeFriezeBoardMaterial"],
      };
    }
  }
  if (consolidatedEaveFriezeBoardInput.length > 0) {
    finalDormerSettings = {
      ...finalDormerSettings,
      eaveFriezeBoard: consolidatedEaveFriezeBoardInput,
      eaveFriezeBoardMaterialLegacy: dormerSettings["eaveFriezeBoardMaterial"],
    };
    if (dormerSettings["eaveFriezeBoardMaterial"]) {
      finalDormerSettings = {
        ...finalDormerSettings,
        eaveFriezeBoardMaterialLegacy: dormerSettings["eaveFriezeBoardMaterial"],
      };
    }
  }

  const finalRoofSettings: Partial<RoofSettings> = {};

  for (const [key, value] of Object.entries(finalDormerSettings)) {
    if (roofSettingsKeys[key] && value !== undefined) {
      finalRoofSettings[key] = value;
    }
  }
  return finalRoofSettings as RoofSettings;
}

export function getFinalDormerSettings(
  instanceSettings: Partial<DormerSettings> | undefined,
  dormerSettings: DormerSettings,
): DormerSettings {
  const filteredInstanceSettings: Partial<DormerSettings> = {};

  for (const id in instanceSettings) {
    const val = instanceSettings[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  return {
    ...dormerSettings,
    ...filteredInstanceSettings,
  };
}

export function getFinalDormerFramingSettings(
  dormer: RoofDormerPlacement,
  roofFramingSettings: RoofFramingSettings,
): RoofFramingSettings {
  const finalSettings = {
    ...roofFramingSettings,
  };

  const filteredInstanceSettings: Partial<RoofSettings> = {};

  for (const id in dormer) {
    const val = dormer[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  return {
    ...finalSettings,
    ...filteredInstanceSettings,
  } as RoofFramingSettings;
}

interface AddEaveExtensionOptions {
  readonly subRoof: GeneratedRoof;
  readonly rail3: PolyLine3;
  readonly eaveSlope: number;
  readonly roofOverhang: number;
  readonly isEnd: boolean;
  readonly extension: number;
  readonly side: RoofSide;
  readonly exteriorWallOffsetWithFinish: number;
  readonly eaveExtensions?: EaveExtension[];
  readonly revert?: boolean;
  readonly isRakeExtension?: boolean;
  readonly faceOffsetZ?: number;
}

export function addEaveExtension({
  subRoof,
  rail3,
  eaveSlope,
  roofOverhang,
  isEnd,
  extension,
  side,
  exteriorWallOffsetWithFinish,
  eaveExtensions = [],
  revert = false,
  isRakeExtension = false,
  faceOffsetZ = 0,
}: AddEaveExtensionOptions): EaveExtension[] | undefined {
  const { isDormer, roofPlanSettings, roofElevationPlanSettings } = subRoof;
  if (roofOverhang < DIST_EPSILON || extension < DIST_EPSILON) {
    return undefined;
  }

  const eaveRail3 = polyLine3Edge(rail3, isEnd ? -1 : 0);
  const originalLength = polyLine3Length(eaveRail3);

  if (isEnd) {
    polyLine3ExtendEnd(eaveRail3, extension);
    polyLine3ExtendStart(eaveRail3, -originalLength);
  } else {
    polyLine3ExtendStart(eaveRail3, extension);
    polyLine3ExtendEnd(eaveRail3, -originalLength);
  }

  if (revert) {
    polyLine3Reverse(eaveRail3);
  }

  const bottomRail3 = offsetPolyLine3InXYPlane(eaveRail3, roofOverhang);
  const topRail3 = polyLine3Move(polyLine3Copy(eaveRail3), 0, 0, roofOverhang * eaveSlope);

  const face = isRakeExtension
    ? faceByFourPoints(
        bottomRail3[3],
        bottomRail3[4],
        bottomRail3[5],
        topRail3[3],
        topRail3[4],
        topRail3[5],
        topRail3[0],
        topRail3[1],
        topRail3[2],
        bottomRail3[0],
        bottomRail3[1],
        bottomRail3[2],
      )
    : faceByFourPoints(
        bottomRail3[0],
        bottomRail3[1],
        bottomRail3[2],
        bottomRail3[3],
        bottomRail3[4],
        bottomRail3[5],
        topRail3[3],
        topRail3[4],
        topRail3[5],
        topRail3[0],
        topRail3[1],
        topRail3[2],
      );

  if (face) {
    faceOrNullMove(face, 0, 0, faceOffsetZ);

    const roofTopFace = roofFaceNew(
      RoofFaceType.NORMAL,
      side,
      face,
      isDormer,
      { ...roofPlanSettings, ...roofElevationPlanSettings },
      true,
    );

    const topRail2 = planeProjectPolyLine3(planeNewXY(), eaveRail3);
    eaveExtensions.push(eaveExtensionNew(roofTopFace, topRail2, eaveRail3, exteriorWallOffsetWithFinish));
  }

  return undefined;
}

// Roof-specific names for callers below; the composition itself lives in profileTools.ts so
// non-roof pickers (e.g. ExteriorTrimSettingsControls, PorchSettingsControls) share it too.
export function getRoofFriezeBoardValues(profileTable: ProfileTable, settingsToCatalogs: SettingsToCatalogs): string[] {
  return getProfileValuesWithNone(profileTable, settingsToCatalogs);
}

export function getRoofWallBaseBoardValues(
  profileTable: ProfileTable,
  settingsToCatalogs: SettingsToCatalogs,
): string[] {
  return getProfileValues(profileTable, settingsToCatalogs);
}

export function getRoofBellyBoardValues(profileTable: ProfileTable, settingsToCatalogs: SettingsToCatalogs): string[] {
  return getProfileValuesWithNone(profileTable, settingsToCatalogs);
}

export function getRoofProfileValues(profileTable: ProfileTable, settingsToCatalogs: SettingsToCatalogs): string[] {
  return getProfileValuesWithNone(profileTable, settingsToCatalogs);
}

export function getHighestRoofPoint(topRoofFaceInfos: Table<RoofFace[]>, plane: Plane) {
  let highestRoofPoint = Number.NEGATIVE_INFINITY;

  for (const levelId in topRoofFaceInfos) {
    const faces = topRoofFaceInfos[levelId];
    for (const { face } of faces) {
      const loop = faceProjectOuterLoopToPlane(face, plane);
      for (let i = 1, l = loop.length; i < l; i += 2) {
        const y = loop[i];
        if (y > highestRoofPoint) {
          highestRoofPoint = y;
        }
      }
    }
  }
  return highestRoofPoint;
}
